import { ReactElement, useEffect, useState, useContext } from 'react'
import MapGl, { Marker, useMap, GeolocateControl } from 'react-map-gl'
import styled, { ThemeContext } from 'styled-components'
import { device } from '../breakpoints'
import IcomoonReact from 'icomoon-react'
import iconSet from './icons.json'

const isMobile = window.innerWidth < 768
// Fly to the active marker
const FlyManager = ({ lat, lng }: { lat: number; lng: number }) => {
  const { current: map } = useMap()

  if (lat && lng && map) {
    map.flyTo({ center: [lng, lat] })
  }

  return <></>
}

const Map = ({
  handleMarkerClick,
  markers,
  handleMapLoaded,
  handleUserLocationFound,
  handleUserLocationError,
  activeMarker,
  locked,
  publisherDomain,
  locateMe,
}: {
  handleMarkerClick: (marker: any) => void
  markers: any[]
  handleMapLoaded: () => void
  handleUserLocationFound: (lat: number, lng: number) => void
  handleUserLocationError: (e: GeolocationPositionError) => void
  activeMarker: any
  locked: boolean
  publisherDomain: string | undefined
  locateMe: boolean
}): ReactElement => {
  const [geolocateControlRef, setGeolocateControlRef] = useState<any>(null)
  const themeContext = useContext(ThemeContext)

  // Get the user location
  useEffect(() => {
    if (locateMe && geolocateControlRef) {
      console.log('Trigger geolocateControl')
      geolocateControlRef.trigger()
    }
  }, [locateMe, geolocateControlRef])

  // We need to center and zoom the map according to the tenant
  let center = [6.627086027591877, 46.52656029698641]
  if (publisherDomain === 'tdg') center = [6.148, 46.2]
  else if (publisherDomain === 'tagesanzeiger') center = [8.542, 47.375]
  else if (publisherDomain === 'bernerzeitung' || publisherDomain === 'derbund') center = [7.578, 46.929]
  // 46.85043241891039, 7.575795092064601
  // statt [7.518, 46.929]

  let zoom = isMobile ? 11 : 12
  if (publisherDomain === 'tdg') zoom = isMobile ? 12 : 13
  else if (publisherDomain === 'tagesanzeiger') zoom = isMobile ? 12 : 13
  else if (publisherDomain === 'bernerzeitung' || publisherDomain === 'derbund') zoom = isMobile ? 7.6 : 8

  const mapStyle = (publisherDomain === 'tdg' || publisherDomain === '24heures')
    ? themeContext?.colorMode === 'dark' ? 'cldjzzf89000n01o4huj5ypu2' : 'cl0cgrlms001e14rtvd7sxxq7'
    : themeContext?.colorMode === 'dark' ? 'cmd74g3ad00gz01sf72w6fxua' : 'cmd74fosc00gy01sfc1p54ho6'


  return (
    <Container>
      <MapGl
        scrollZoom={isMobile ? true : !locked}
        dragPan={isMobile ? true : !locked}
        pitchWithRotate={false}
        dragRotate={false}
        // touchZoomRotate={false}
        onLoad={() => {
          handleMapLoaded()
        }}
        initialViewState={{
          longitude: center[0],
          latitude: center[1],
          zoom: zoom,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={`mapbox://styles/ta-interaktiv/${mapStyle}`}
        mapboxAccessToken='pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJja292ZWk5ZnIwN2w0MndwZWQ0Nmpqcm1iIn0.d1OaviBwQQGO1_roYeaimQ'
      >
        <GeolocateControl
          ref={setGeolocateControlRef}
          position='bottom-right'
          onGeolocate={(res) => {
            handleUserLocationFound(res.coords.latitude, res.coords.longitude)
          }}
          onError={(e: GeolocationPositionError) => {
            handleUserLocationError(e)
          }}
        />
        {activeMarker && (
          <FlyManager
            lat={activeMarker.latitude}
            lng={activeMarker.longitude}
          />
        )}
        {markers.map((marker) => (
          <Marker
            key={marker.markerId}
            longitude={marker.longitude}
            latitude={marker.latitude}
            anchor='center'
            style={{
              zIndex: marker.markerId === activeMarker?.markerId ? 1 : 0,
            }}
          >
            <MarkerContent
              active={marker.markerId === activeMarker?.markerId}
              onClick={() => handleMarkerClick(marker)}
            >
              <IcomoonReact
                iconSet={iconSet}
                color={
                  marker.markerId === activeMarker?.markerId
                    ? '#ffffff'
                    : themeContext.textColor
                }
                size={22}
                icon={
                  marker.typeShort === 'SF' ? 'takeaway-icon' : 'resto-icon'
                }
              />
            </MarkerContent>
          </Marker>
        ))}
      </MapGl>
    </Container>
  )
}

export default Map

const Container = styled.div`
  position: relative;
  background-color: #f5f6f7;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 100%;
    margin-left: 0%;
    height: 100%;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 100%;
    margin-left: 0%;
    height: 100vh;
  }
`

const MarkerContent = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ active, theme }) =>
    active ? theme.mainColor : theme.plainColor};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  cursor: pointer;
`
